import moment from 'moment';
import zatcaApi from './zatcaApi';

// Turning an RMS sales invoice into a ZATCA standard (B2B) tax invoice.
//
// Everything ZATCA-specific lives here so the invoice page only has to say
// "this was submitted, clear it". The page never builds a payload itself.
//
// What we send: what was sold, to whom, and when it was supplied. What we never
// send: invoice number, UUID, counter (ICV), hashes, signature or QR — ZatcaTest
// owns all of that, and there is no field to override it.

// TEMPORARY. ZATCA's own sample buyer, so the flow can be tested end to end
// before the RMS customer API exists. Replace with the real customer as soon as
// that API is available — see CUSTOMER-API-REQUEST.md in this folder.
export const SAMPLE_BUYER = {
    legalName: 'Fatoora Samples LTD',
    vatRegistrationNumber: '399999999800003',
    streetName: 'King Fahd Road',
    buildingNumber: '1234',
    plotIdentification: '5678',
    citySubdivisionName: 'Al Olaya',
    cityName: 'Riyadh',
    postalZone: '12244',
    countryCode: 'SA'
};

// The container field labels the invoice page uses. Fields are identified by
// their fieldDescription there, so they are identified the same way here.
const ITEM_FIELD = 'Name of Good or Service';
const QUANTITY_FIELD = 'Quantity';
const UNIT_PRICE_FIELD = 'Unit Price';
const DISCOUNT_FIELD = 'Discount';

// Tried in order for the supply date. The first one the container actually has,
// with a real value, wins; if none do, ZatcaTest uses the issue date.
// const SUPPLY_DATE_FIELDS = ['Supply Date', 'Date of Supply', 'Invoice Date', 'Document Date'];
const SUPPLY_DATE_FIELDS = [ 'Date of Supply'];

const num = (value) => {
    const parsed = Number(value);
    return isNaN(parsed) ? 0 : parsed;
};

/**
 * The invoice page's Unit Price excludes VAT: GetService returns priceperunit and
 * the page adds tax on top of it. Saying so is all the frontend does about tax —
 * it states the basis of the prices it is sending, and ZatcaTest derives every
 * tax figure from there.
 *
 * Nothing here computes a rate, a tax amount or a total. Sending a rate would
 * mean sending the item master's value, which is how a 10% rate reached ZATCA and
 * was rejected: standard-rated VAT in Saudi is 15% or 5%, and the API knows that.
 */
const PRICING_BASIS = 'VatExclusive';

const fieldsOf = (process) =>
    (process || []).reduce((all, stage) => all.concat((stage && stage.stage_Field) || []), []);

const valueOf = (row, description) => {
    const field = row.filter((f) => f.fieldDescription === description)[0];
    return field ? field.fieldValue : null;
};

// Dropdown fields hold the option's value; the invoice needs its label.
const labelOf = (metaData, description, value) => {
    const options = (metaData || {})[description] || [];
    const match = options.filter((option) => option.value === value)[0];
    return (match && match.label) || '';
};

/**
 * The line-item rows of the container, oldest first.
 *
 * The highest childRowNmbr is the row still being typed on screen, not a line of
 * the invoice — the page's own DataTable leaves it out the same way — so it is
 * dropped here too.
 */
const lineRowsOf = (process) => {
    const fields = fieldsOf(process);
    const containerNames = fields.filter((f) => f.fieldType === '9').map((f) => f.fieldName);
    const children = fields.filter((f) => containerNames.indexOf(f.parentFieldId) !== -1);

    const rows = children.reduce((grouped, field) => {
        const key = String(field.childRowNmbr);
        grouped[key] = (grouped[key] || []).concat(field);
        return grouped;
    }, {});

    const keys = Object.keys(rows).sort((a, b) => Number(a) - Number(b));
    return keys.slice(0, Math.max(keys.length - 1, 0)).map((key) => rows[key]);
};

/**
 * The invoice lines, built from the page's container rows.
 *
 * What was sold, how many, and at what price — nothing about tax. No taxRatePercent
 * and no taxCategory: ZatcaTest applies the Saudi standard rate, so the item
 * master's rate cannot put an invalid rate on a tax document.
 */
export const buildZatcaLines = (process, metaData) =>
    lineRowsOf(process).reduce((lines, row) => {
        const quantity = num(valueOf(row, QUANTITY_FIELD));
        const itemValue = valueOf(row, ITEM_FIELD);
        const itemName = labelOf(metaData, ITEM_FIELD, itemValue) || itemValue || '';

        // Skips a blank or half-filled row rather than sending something ZATCA
        // would refuse.
        if (!itemName || quantity <= 0) return lines;

        return lines.concat({
            itemName: String(itemName),
            quantity: quantity,
            unitPrice: num(valueOf(row, UNIT_PRICE_FIELD)),
            // Treated on the same basis as the price, i.e. before VAT.
            discount: num(valueOf(row, DISCOUNT_FIELD)),
            unitCode: 'PCE'
        });
    }, []);

/** The supply date as yyyy-MM-dd, or null to let ZatcaTest use the issue date. */
export const readSupplyDate = (process) => {
    const fields = fieldsOf(process);

    for (let i = 0; i < SUPPLY_DATE_FIELDS.length; i++) {
        const field = fields.filter((f) => f.fieldDescription === SUPPLY_DATE_FIELDS[i] && f.fieldValue)[0];
        if (field) {
            const date = moment(field.fieldValue);
            if (date.isValid()) return date.format('YYYY-MM-DD');
        }
    }

    return null;
};

/**
 * The RMS request number, carried to ZATCA as orderReference so the two systems
 * can be reconciled.
 *
 * `requestNum` is what the save and submit responses carry — e.g. "SIV-202609-016".
 * The request id is kept as a fallback: an untraceable reference is worse than a
 * GUID one, but neither should ever be null on a real response.
 */
export const readOrderReference = (responseData) => {
    const data = responseData || {};
    return data.requestNum || data.requestId || null;
};

/**
 * Issues a standard (B2B) tax invoice and clears it with ZATCA. Resolves only
 * when ZATCA has cleared it; a refusal arrives as a rejected promise whose
 * response body carries `errors`.
 *
 * Pass egsUnitId to bill as a branch's own EGS unit; without it the invoice is
 * issued by the unit in ZatcaTest's zatca.config.json.
 */
export const issueStandardInvoice = (options) => {
    const settings = options || {};
    const path = settings.egsUnitId
        ? '/api/units/' + encodeURIComponent(settings.egsUnitId) + '/invoices/standard'
        : '/api/invoices/standard';

    const payload = {
        orderReference: settings.orderReference || null,
        paymentMeansCode: settings.paymentMeansCode || '42',
        // The basis of the prices below. Every tax figure on the invoice is the
        // API's to decide.
        pricing: PRICING_BASIS,
        buyer: settings.buyer || SAMPLE_BUYER,
        lines: settings.lines || []
    };

    if (settings.supplyDate) payload.supplyDate = settings.supplyDate;

    return zatcaApi.post(path, payload);
};

/** What went wrong, in words a user can act on. */
export const describeZatcaError = (error) => {
    const response = (error && error.response) || {};
    const body = response.data || {};

    if (body.errors && body.errors.length > 0) return body.errors.join(' ');
    if (body.error) return body.error;
    if (response.status === 404) return 'This branch has no ZATCA unit set up.';
    if (!response.status) return 'The ZATCA service is not reachable.';

    return 'ZATCA clearance failed (HTTP ' + response.status + ').';
};
